import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { Card, CardSm, Label, Pill, Select } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { Camera, Pulse, ShieldWarning, CheckCircle } from '@phosphor-icons/react';
import { fileToBase64, healthClassToColor, fmt } from '@/lib/utils';

const CLASSES = ['Healthy', 'Mild Stress', 'Moderate Stress', 'Severe Stress', 'Dead/Dying'];

export default function HealthAssessment() {
  const { treeId } = useParams();
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  const [healthImg, setHealthImg] = useState(null);
  const [healthClass, setHealthClass] = useState('Healthy');
  const [healthBusy, setHealthBusy] = useState(false);
  const [disease, setDisease] = useState(null);
  const [diseaseBusy, setDiseaseBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const scanHealth = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setHealthBusy(true); setErr('');
    try {
      const b64 = await fileToBase64(f);
      setHealthImg(b64);
      const { data } = await api.post('/ai/health', {
        image_base64: b64, mime_type: f.type || 'image/jpeg',
      });
      setHealth(data.result);
      if (data.result?.health_class) setHealthClass(data.result.health_class);
    } catch (ex) { setErr(ex?.response?.data?.detail || 'AI health failed'); }
    finally { setHealthBusy(false); }
  };

  const scanDisease = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setDiseaseBusy(true); setErr('');
    try {
      const b64 = await fileToBase64(f);
      const { data } = await api.post('/ai/disease', {
        image_base64: b64, mime_type: f.type || 'image/jpeg',
      });
      setDisease(data.result);
    } catch (ex) { setErr(ex?.response?.data?.detail || 'AI disease failed'); }
    finally { setDiseaseBusy(false); }
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const flags = (disease?.detected || []).map((d) => d.condition);
      await api.post('/measurements', {
        tree_id: treeId,
        event_type: 'health_check',
        canopy_health_class: healthClass,
        canopy_cover_pct: health?.canopy_cover_pct_estimate || null,
        lai_estimate: health?.lai_estimate || null,
        disease_flags: flags,
        ai_health_confidence: health?.confidence || null,
        media: [
          ...(healthImg ? [{ photo_category: 'canopy', image_base64: healthImg, mime_type: 'image/jpeg' }] : []),
        ],
      });
      nav(`/trees/${treeId}`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="health-assessment">
      <TopBar title="Health Assessment" back />
      <div className="p-4 space-y-4 pb-24">
        {/* Canopy health */}
        <Card data-testid="canopy-health-card">
          <div className="flex items-center gap-2 mb-2">
            <Pulse size={18} weight="bold" />
            <span className="font-display font-bold uppercase tracking-widest text-xs">Canopy Health (MHI)</span>
          </div>

          <label className="block">
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={scanHealth} data-testid="scan-health-file" />
            <Button as="span" variant="dark" className="w-full cursor-pointer" data-testid="scan-health-btn">
              <Camera size={20} weight="bold" />
              {healthBusy ? 'Analyzing canopy…' : 'Scan health (canopy photo)'}
            </Button>
          </label>

          {health && (
            <div className="mt-3 space-y-2" data-testid="health-result">
              <div className="flex items-center justify-between">
                <Pill color={healthClassToColor(health.health_class)}>{health.health_class}</Pill>
                <span className="mono text-xs">{((health.confidence || 0) * 100).toFixed(0)}% conf.</span>
              </div>
              {/* Confidence bar */}
              <div className="h-2 border-2 border-carbon bg-white">
                <div className="h-full bg-brand-primary" style={{ width: `${(health.confidence || 0) * 100}%` }} />
              </div>
              <p className="mono text-xs">
                Canopy cover: {fmt(health.canopy_cover_pct_estimate, 0)}% · LAI: {fmt(health.lai_estimate, 2)}
              </p>
              {health.indicators?.length > 0 && (
                <ul className="text-xs space-y-0.5">
                  {health.indicators.map((i, idx) => <li key={idx}>• {i}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="mt-3">
            <Label>Manual override</Label>
            <Select value={healthClass} onChange={(e) => setHealthClass(e.target.value)} data-testid="health-class-select">
              {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
        </Card>

        {/* Disease */}
        <Card data-testid="disease-card">
          <div className="flex items-center gap-2 mb-2">
            <ShieldWarning size={18} weight="bold" />
            <span className="font-display font-bold uppercase tracking-widest text-xs">Disease & Pest Detection</span>
          </div>

          <label className="block">
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={scanDisease} data-testid="scan-disease-file" />
            <Button as="span" variant="outline" className="w-full cursor-pointer" data-testid="scan-disease-btn">
              <Camera size={20} weight="bold" />
              {diseaseBusy ? 'Analyzing…' : 'Scan close-up (leaf/bark)'}
            </Button>
          </label>

          {disease && (
            <div className="mt-3" data-testid="disease-result">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-display text-xs font-bold uppercase tracking-widest">Severity:</span>
                <Pill>{disease.overall_severity || 'none'}</Pill>
              </div>
              {(disease.detected || []).length > 0 ? (
                <ul className="space-y-1">
                  {disease.detected.map((d, i) => (
                    <li key={i} className="text-xs mono flex items-center justify-between">
                      <span>{d.condition}</span>
                      <span>{d.severity} · {Math.round((d.confidence || 0) * 100)}%</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-carbon-soft">No issues detected.</p>
              )}
              {disease.notes && <p className="text-xs mt-2">{disease.notes}</p>}
            </div>
          )}
        </Card>

        {err && <div className="border-2 border-status-severe bg-red-50 px-3 py-2 text-sm text-status-severe">{err}</div>}

        <Button onClick={save} disabled={saving} size="lg" className="w-full" data-testid="save-health-btn">
          {saving ? 'Saving…' : 'Save health event'}
          <CheckCircle size={20} weight="bold" />
        </Button>
      </div>
    </div>
  );
}
